// Tipos básicos del dominio. Cada esquema zod y su tipo llevan el MISMO nombre (z.infer).
// Regla: los esquemas de entrada usan z.strictObject (un campo de más TRUENA, no se tira en silencio).
import { z } from 'zod';

export const Id = z.string().regex(/^[A-Za-z0-9_-]{6,64}$/, 'identificador no válido');
export type Id = z.infer<typeof Id>;

export const Fecha = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/, 'fecha AAAA-MM-DD');
export type Fecha = z.infer<typeof Fecha>;

export const FechaHora = z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,9})?(Z|[+-]\d{2}:\d{2})$/, 'fecha y hora ISO 8601');
export type FechaHora = z.infer<typeof FechaHora>;

export const Correo = z
  .string()
  .trim()
  .toLowerCase()
  .max(254)
  .regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'correo no válido');
export type Correo = z.infer<typeof Correo>;

/** Pesos mexicanos, no negativos, finitos. */
export const Pesos = z.number().nonnegative();
export type Pesos = z.infer<typeof Pesos>;

/** WhatsApp como lo usa la plantilla: 52 + 10 dígitos. */
export const WhatsApp = z.string().regex(/^52\d{10}$/, 'WhatsApp: 52 y 10 dígitos');

/** Tarifas de CFE. Los recibos impresos a veces pegan la región ("1FNO"): la IA normaliza a estos valores. */
export const TARIFAS = ['1', '1A', '1B', '1C', '1D', '1E', '1F', 'DAC', 'PDBT', 'GDBT', 'GDMTO', 'GDMTH', 'RABT', 'RAMT', 'APBT', 'APMT'] as const;
export const Tarifa = z.enum(TARIFAS);
export type Tarifa = z.infer<typeof Tarifa>;
export const TARIFAS_CASA: readonly Tarifa[] = ['1', '1A', '1B', '1C', '1D', '1E', '1F', 'DAC'];
export const TARIFAS_NEGOCIO: readonly Tarifa[] = ['PDBT', 'GDBT', 'GDMTO', 'GDMTH'];
export const TARIFAS_MEDIA_TENSION: readonly Tarifa[] = ['GDMTO', 'GDMTH'];

/** Tipo de proyecto del cuestionario. "baterias" = híbrido o respaldo con baterías; "aislado" = sin red de CFE.
 *  "ampliacion_baterias" (Diego, 27-oct-2026): sumar baterías a un sistema que ya tiene; no pasa por el motor solar (sin
 *  recibo ni opciones): su captura es `contenido.piezas` y sale en el formato «piezas» (dominio/ampliacion-baterias.ts). */
export const TIPOS_PROYECTO = ['casa', 'comercio', 'industria', 'baterias', 'aislado', 'ampliacion_baterias'] as const;
export const TipoProyecto = z.enum(TIPOS_PROYECTO);
export type TipoProyecto = z.infer<typeof TipoProyecto>;

/** Precotización = sin visita técnica; final = con visita técnica hecha. */
export const Etapa = z.enum(['precotizacion', 'final']);
export type Etapa = z.infer<typeof Etapa>;

export const Facturacion = z.enum(['mensual', 'bimestral']);
export type Facturacion = z.infer<typeof Facturacion>;

/** Medidor A (el principal) y B (solo con dos medidores). */
export const Medidor = z.enum(['A', 'B']);
export type Medidor = z.infer<typeof Medidor>;

export const OPCIONES = ['arranque', 'exacta', 'holgada'] as const;
export const OpcionId = z.enum(OPCIONES);
export type OpcionId = z.infer<typeof OpcionId>;

/** Formatos de la familia de PDF (ESPEC 5.5). Hoy (9-oct) solo "residencial" está registrado en el generador. */
// 'consolidado' (ADENDA z68-3, 31-oct-2026): el «todo en uno» con casas y bimestrales. Se GUARDA en PdfVersion.formato: la vuelta
// atrás a una versión sin él tiene que aceptarlo (ESTADO-HOY95).
export const FORMATOS_FAMILIA = ['residencial', 'comercial', 'industrial', 'baterias', 'horaria', 'desglose', 'piezas', 'multisitio', 'consolidado'] as const;
export const FormatoFamilia = z.enum(FORMATOS_FAMILIA);
export type FormatoFamilia = z.infer<typeof FormatoFamilia>;

/** Variantes de la familia (ESPEC §4). "ampliacion" (ya tiene paneles y quiere más) la pidió Diego aparte: la familia
 *  la construye en la fase de variantes; hasta que el generador la registre, el puente NO la manda (truena el PDF). */
/** base_futura (ADENDA z52, 28-oct-2026): «pagarías sin paneles» lo calcula el cotizador con otra base que el recibo (negocio con
 *  recibo de casa en PDBT y/o aumento de consumo aceptado); el recibo real va en una línea. */
export const VARIANTES = ['dos_medidores', 'consumo_proyectado', 'aumento_consumo', 'historial_incompleto', 'ampliacion', 'base_futura'] as const;
export const Variante = z.enum(VARIANTES);
export type Variante = z.infer<typeof Variante>;

/** Niveles del formato de baterías (ESPEC 3.4): Esencial / Confort / Casa completa. */
export const NIVELES_RESPALDO = ['esencial', 'confort', 'completa'] as const;
export const NivelRespaldo = z.enum(NIVELES_RESPALDO);
export type NivelRespaldo = z.infer<typeof NivelRespaldo>;

/** Texto corto obligatorio (sin espacios a los lados). */
export const Texto = (min: number, max: number) => z.string().trim().min(min).max(max);
